/**
 * Declarative filter bar.
 *
 * A page describes its filters as data:
 *
 *   <FilterBar listState={list} filters={[
 *     { key: 'status', type: 'enum', enum: 'policyStatus', multiple: true },
 *     { key: 'insuranceCompanyId', type: 'options', options: insurers },
 *     { key: 'expirationMonth', type: 'month' },
 *   ]} />
 *
 * …and gets URL persistence, active-filter chips, a clear-all affordance and a
 * responsive layout for free. No page builds its own filter row, so they cannot drift
 * apart in behaviour or spacing.
 */

import * as React from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox, Popover, PopoverContent, PopoverTrigger, Separator } from '@/components/ui/misc';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useI18n } from '@/lib/i18n';
import { useEnumOptions } from '@/components/common/enum-badge';
import type { EnumName } from '@/lib/registry/enums';
import { cn } from '@/lib/utils';
import { useDebouncedSearch, type ListState } from '@/hooks/use-list-state';

export interface FilterOption {
  value: string;
  label: string;
}

export type FilterDescriptor = (
  | { key: string; type: 'enum'; enum: EnumName; label?: string; multiple?: boolean; width?: string }
  /**
   * `clearable: false` drops the "any value" entry from a single-select. Use it only where
   * every value is a real choice and one is always applied — the dashboard's period, where
   * an entry reading «Περίοδος» among a list of periods offers to clear a filter that the
   * page immediately reinstates as its default.
   */
  | { key: string; type: 'options'; options: FilterOption[]; label: string; multiple?: boolean; width?: string; loading?: boolean; clearable?: boolean }
  | { key: string; type: 'date'; label: string }
  | { key: string; type: 'month'; label: string }
  | { key: string; type: 'text'; label: string; placeholder?: string }
  /** On/off chip — present in the URL as `key=true` when on, absent when off. */
  | { key: string; type: 'switch'; label: string }
) & {
  /**
   * The column this filter belongs under, by column id.
   *
   * Set it and the control moves into the table's own filter row, directly beneath the
   * header it narrows — which is what "φίλτρο ανά στήλη" asks for, and what makes a
   * twelve-column sheet workable without first learning which toolbar dropdown maps to
   * which column. Unbound filters (no column, or a column not on screen) stay in the
   * toolbar, so a filter can never become unreachable by being hidden with its column.
   */
  column?: string;
};

interface FilterBarProps {
  listState: ListState;
  filters: FilterDescriptor[];
  searchPlaceholder?: string;
  /** Buttons rendered on the right — "New policy", export, column toggle. */
  actions?: React.ReactNode;
  className?: string;
  /**
   * Which filters this bar renders inline on a wide screen.
   *
   * `all` is the original behaviour. `unbound` leaves anything with a `column` to the
   * table's filter row — pass it on a screen that renders one, so the same control is not
   * offered twice. The narrow-screen popover always holds every filter either way: the
   * column row is a wide-screen affordance, and a filter that vanished on a tablet would
   * be a filter the user cannot clear.
   */
  inline?: 'all' | 'unbound';
}

export function FilterBar({
  listState,
  filters,
  searchPlaceholder,
  actions,
  className,
  inline = 'all',
}: FilterBarProps) {
  const { t } = useI18n();
  const [searchValue, setSearchValue] = useDebouncedSearch(listState.search, listState.setSearch);

  // Below `lg` the filters collapse into a popover so the toolbar stays one row on a
  // laptop and does not become a wall of dropdowns on a tablet.
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const controls = filters.map((filter) => (
    <FilterControl key={filter.key} filter={filter} listState={listState} />
  ));

  const inlineControls =
    inline === 'all'
      ? controls
      : filters
          .filter((filter) => !filter.column)
          .map((filter) => <FilterControl key={filter.key} filter={filter} listState={listState} />);

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="group relative min-w-[13rem] flex-1 sm:max-w-xs">
          <Search className="text-muted-soft pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 transition-opacity duration-150 group-focus-within:opacity-100" />
          <Input
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder={searchPlaceholder ?? t('action.search')}
            className="pl-8.5"
            aria-label={t('action.search')}
          />
          {searchValue && (
            <button
              type="button"
              onClick={() => setSearchValue('')}
              className="text-muted-soft hover:bg-accent hover:text-foreground absolute top-1/2 right-1.5 -translate-y-1/2 rounded-md p-1 transition-colors duration-150"
              aria-label={t('action.clear')}
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>

        {inlineControls.length > 0 && (
          <div className="hidden flex-wrap items-center gap-2 lg:flex">{inlineControls}</div>
        )}

        {filters.length > 0 && (
          <Popover open={mobileOpen} onOpenChange={setMobileOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="lg:hidden">
                <SlidersHorizontal />
                {t('action.filter')}
                {listState.activeFilterCount > 0 && (
                  <Badge tone="neutral" size="sm" className="tabular">
                    {listState.activeFilterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 space-y-3">{controls}</PopoverContent>
          </Popover>
        )}

        {listState.activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={listState.clearFilters}>
            <X /> {t('action.clearAll')}
          </Button>
        )}

        {actions && (
          <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>
        )}
      </div>
    </div>
  );
}

/**
 * One filter control.
 *
 * `compact` is the in-table variant: shorter, full-bleed in its column cell, and without
 * the toolbar's fixed widths — the column decides how wide the control is, because the
 * control sits under that column's own heading.
 */
export function FilterControl({
  filter,
  listState,
  compact = false,
}: {
  filter: FilterDescriptor;
  listState: ListState;
  compact?: boolean;
}) {
  const { t } = useI18n();
  const current = listState.filters[filter.key] ?? '';

  if (filter.type === 'enum' || filter.type === 'options') {
    return <OptionFilter filter={filter} listState={listState} compact={compact} />;
  }

  if (filter.type === 'switch') {
    const on = current === 'true';
    return (
      <Button
        variant={on ? 'secondary' : 'outline'}
        size="sm"
        className={cn(compact ? 'h-8 w-full justify-start font-normal' : 'h-9')}
        aria-pressed={on}
        onClick={() => listState.setFilter(filter.key, on ? undefined : 'true')}
      >
        <Checkbox checked={on} className="pointer-events-none size-3.5" tabIndex={-1} />
        <span className="truncate">{filter.label}</span>
      </Button>
    );
  }

  if (filter.type === 'date' || filter.type === 'month') {
    return (
      <div className={cn('flex items-center gap-1.5', compact && 'w-full')}>
        <Input
          type={filter.type === 'month' ? 'month' : 'date'}
          value={current}
          onChange={(event) => listState.setFilter(filter.key, event.target.value)}
          aria-label={filter.label}
          className={cn(compact ? 'h-8 w-full min-w-0 text-[12.5px]' : 'h-9 w-[9.5rem]')}
        />
        {current && (
          <Button
            variant="ghost"
            size="icon-sm"
            className={cn(compact && 'size-7 shrink-0')}
            onClick={() => listState.setFilter(filter.key, undefined)}
            aria-label={t('action.clear')}
          >
            <X />
          </Button>
        )}
      </div>
    );
  }

  return <TextFilter filter={filter} listState={listState} compact={compact} />;
}

/**
 * Free-text column filter.
 *
 * Debounced, unlike the toolbar version it replaces: a control that lives in the header
 * row invites typing, and an un-debounced one fired a request per keystroke — visible as
 * the table's refetch bar strobing while a plate number was being entered.
 */
function TextFilter({
  filter,
  listState,
  compact,
}: {
  filter: Extract<FilterDescriptor, { type: 'text' }>;
  listState: ListState;
  compact?: boolean;
}) {
  const committed = listState.filters[filter.key] ?? '';
  const [value, setValue] = useDebouncedSearch(committed, (next) =>
    listState.setFilter(filter.key, next || undefined),
  );

  return (
    <Input
      value={value}
      onChange={(event) => setValue(event.target.value)}
      placeholder={filter.placeholder ?? filter.label}
      aria-label={filter.label}
      className={cn(compact ? 'h-8 w-full min-w-0 text-[12.5px]' : 'h-9 w-40')}
    />
  );
}

/** Single- or multi-select over enum values or a supplied option list. */
function OptionFilter({
  filter,
  listState,
  compact = false,
}: {
  filter: Extract<FilterDescriptor, { type: 'enum' | 'options' }>;
  listState: ListState;
  compact?: boolean;
}) {
  const { t } = useI18n();
  const enumOptions = useEnumOptions(filter.type === 'enum' ? filter.enum : 'role');
  const options: FilterOption[] = filter.type === 'enum' ? enumOptions : filter.options;
  const label = filter.type === 'enum' ? (filter.label ?? t(`policy.${filter.key}`)) : filter.label;

  const raw = listState.filters[filter.key] ?? '';
  const selected = raw ? raw.split(',') : [];

  if (!filter.multiple) {
    return (
      <Select
        value={raw || '__all'}
        onValueChange={(value) => listState.setFilter(filter.key, value === '__all' ? undefined : value)}
      >
        <SelectTrigger
          size="sm"
          className={cn(
            compact ? 'h-8 w-full min-w-0 text-[12.5px]' : 'h-9 w-auto min-w-[9rem]',
            !compact && filter.width,
          )}
        >
          <SelectValue placeholder={label} />
        </SelectTrigger>
        <SelectContent>
          {(filter.type === 'enum' || filter.clearable !== false) && (
            <SelectItem value="__all">{label}</SelectItem>
          )}
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  const toggle = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value];
    listState.setFilter(filter.key, next.length ? next : undefined);
  };

  const hasSelection = selected.length > 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        {/*
          An engaged filter is signalled by weight, not by colour: the border firms up
          and the label goes to full ink. The value chips stay neutral — a row of blue
          pills across the toolbar was one of the loudest uses of the accent in the app,
          and it was spending it on something the border already says.
        */}
        <Button
          variant="outline"
          size="sm"
          className={cn(
            compact ? 'h-8 w-full min-w-0 justify-start px-2 text-[12.5px] font-normal' : 'h-9',
            hasSelection ? 'border-border-strong text-foreground bg-accent/50' : 'text-muted-foreground',
            !compact && filter.width,
          )}
        >
          {/* In a column cell the heading above already names the field, so a selection
              replaces the label rather than sitting beside it — two words plus two chips
              does not fit a 9rem column, and the label is the half that is redundant. */}
          {compact && hasSelection ? (
            <span className="flex min-w-0 items-center gap-1">
              {selected.length === 1 ? (
                <span className="truncate">
                  {options.find((option) => option.value === selected[0])?.label ?? selected[0]}
                </span>
              ) : (
                <>
                  <Badge tone="neutral" size="sm" className="tabular">
                    {selected.length}
                  </Badge>
                  <span className="text-muted-foreground truncate">{label}</span>
                </>
              )}
            </span>
          ) : (
            <>
              <span className="truncate">{label}</span>
              {hasSelection && (
                <>
                  <Separator orientation="vertical" className="bg-border-strong mx-0.5 h-3.5" />
                  {selected.length <= 2 ? (
                    selected.map((value) => (
                      <Badge key={value} tone="neutral" size="sm">
                        {options.find((option) => option.value === value)?.label ?? value}
                      </Badge>
                    ))
                  ) : (
                    <Badge tone="neutral" size="sm" className="tabular">
                      {selected.length}
                    </Badge>
                  )}
                </>
              )}
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1.5">
        <div role="listbox" aria-multiselectable className="max-h-72 overflow-y-auto">
          {options.map((option) => {
            const checked = selected.includes(option.value);
            return (
              /*
               * A div with `role="option"`, not a <button>. Radix renders its Checkbox as
               * a <button role="checkbox">, and a button inside a button is invalid DOM —
               * React warns, and browsers are free to reparent the markup. The row keeps
               * full keyboard operation through the explicit handlers below.
               */
              <div
                key={option.value}
                role="option"
                aria-selected={checked}
                tabIndex={0}
                onClick={() => toggle(option.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggle(option.value);
                  }
                }}
                className="hover:bg-accent focus-visible:ring-ring/35 flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-[13.5px] transition-colors duration-150 outline-none focus-visible:ring-2"
              >
                <Checkbox checked={checked} className="pointer-events-none" tabIndex={-1} />
                <span className="flex-1 truncate">{option.label}</span>
              </div>
            );
          })}
        </div>
        {hasSelection && (
          <>
            <Separator className="my-1.5" />
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-center"
              onClick={() => listState.setFilter(filter.key, undefined)}
            >
              {t('action.clear')}
            </Button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
